import { getAuthHeaders, getAuthToken, getCurrentUser } from "./authService";

const domain = process.env.REACT_APP_DOMAIN;

export async function fetchLeads({
  page = 1,
  limit = 10,
  search = "",
  status = "",
  deal_status = "",
  agentId = null,
  date_from = "",
  date_to = "",
}) {
  const params = new URLSearchParams({ page, limit });
  if (status) params.set("status", status);
  if (deal_status) params.set("deal_status", deal_status);
  if (search) params.set("search", search);
  if (agentId) params.set("agent_id", agentId);
  if (date_from) params.set("date_from", date_from);
  if (date_to) params.set("date_to", date_to);

  const response = await fetch(`${domain}/api/v1/lead/get?${params.toString()}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return response.json();
}

export async function fetchLeadsForCurrentUser({
  page = 1,
  search = "",
  status = "",
  agentId = null,
  date_from = "",
  date_to = "",
} = {}) {
  try {
    const userData = await getCurrentUser();
    if (!userData?.success || !userData.user?.id) throw new Error("Failed to fetch current user");

    const data = await fetchLeads({ page, limit: 10, search, status, agentId, date_from, date_to });
    if (!data?.success) throw new Error(data?.error || "Failed to fetch leads");

    return {
      leads: data.leads,
      totalPages: data.totalPages,
      page: data.page,
      dealStatusStats: data.dealStatusStats,
      total: data.total,
    };
  } catch (err) {
    return { leads: [], totalPages: 1, page: 1, dealStatusStats: {} };
  }
}

export async function fetchUnassignedLeads({ page = 1, limit = 100, search = "" } = {}) {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("limit", String(limit));
  params.set("unassigned", "true");
  if (search) params.set("search", search);

  const response = await fetch(`${domain}/api/v1/lead/get?${params.toString()}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  const data = await response.json();
  if (!data?.success) return { leads: [], totalPages: 1, page: 1 };
  return { ...data, leads: Array.isArray(data.leads) ? data.leads : [] };
}

export async function fetchAllUnassignedLeads(page = 1, search = "") {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("unassigned", "true");
  if (search) params.set("search", search);

  const response = await fetch(`${domain}/api/v1/lead/get?${params.toString()}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  const data = await response.json();
  if (!data?.success) return { leads: [], totalPages: 1, page: 1 };
  return { ...data, leads: Array.isArray(data.leads) ? data.leads : [] };
}

export async function fetchLeadById(leadId) {
  const res = await fetch(`${domain}/api/v1/lead/get/${leadId}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return res.json();
}

export async function createLead(payload) {
  const res = await fetch(`${domain}/api/v1/lead/create`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return res.json();
}

export const parseVoiceToLead = async (audioBlob) => {
  const token = localStorage.getItem("crm_auth_token");
  const formData = new FormData();
  formData.append("audio", audioBlob, "voice.webm");

  const response = await fetch(`${domain}/api/v1/lead/parse-voice`, {
    method: "POST",
    headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    body: formData,
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.error || "Failed to parse voice");
  return data;
};

export const parseVoiceUpdate = async (audioBlob, leadId) => {
  const token = localStorage.getItem("crm_auth_token");
  const formData = new FormData();
  formData.append("audio", audioBlob, "voice.webm");
  formData.append("lead_id", leadId);

  const response = await fetch(`${domain}/api/v1/lead/parse-voice-update`, {
    method: "POST",
    headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    body: formData,
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.error || "Failed to process voice");
  return data;
};

export const bulkAssignLeads = async (leadIds, agentAssigned) => {
  const response = await fetch(`${domain}/api/v1/lead/bulk-assign`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify({ lead_ids: leadIds, agent_assigned: agentAssigned }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.error || "Failed to assign leads");
  return data;
};

export const updateLead = async (leadId, data) => {
  try {
    const response = await fetch(`${domain}/api/v1/lead/update/${leadId}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    const resData = await response.json();
    if (!response.ok) throw new Error(resData?.error || "Failed to update lead");
    return resData;
  } catch (err) {
    throw err;
  }
};

export const DeleteLead = async (leadId) => {
  try {
    const response = await fetch(`${domain}/api/v1/lead/delete/${leadId}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });
    const resData = await response.json();
    if (!response.ok) throw new Error(resData?.error || "Failed to delete lead");
    return resData;
  } catch (err) {
    throw err;
  }
};

export const fetchAgentLeadStats = async (agentIds) => {
  try {
    const res = await fetch(`${domain}/api/v1/lead/get-lead-stats`, {
      method: "GET",
      headers: getAuthHeaders(),
      body: JSON.stringify({ agentIds }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Failed to fetch stats");
    return data.data;
  } catch (err) {
    return {};
  }
};

export const createBulkLead = async (formData) => {
  const token = getAuthToken();
  if (!token) { console.log("no token found"); return; }
  try {
    const response = await fetch(`${domain}/api/v1/lead/bulk-create`, {
      method: "POST",
      headers: { Authorization: `Bearer ${getAuthToken()}` },
      body: formData,
    });
    if (!response.ok) console.error("Response not OK:", response.status, response.statusText);
    return response.json();
  } catch (error) {
    throw error;
  }
};

// Removed dateCache — it was returning stale data when agent filter changes
export const fetchLeadsByDate = async (date, page = 1, agentId = null) => {
  const params = new URLSearchParams({ date, page });
  if (agentId) params.set("agent_id", agentId); 
  const res = await fetch(
    `${domain}/api/v1/lead/get-lead-by-date?${params.toString()}`,
    { method: "GET", headers: getAuthHeaders() }
  );
  return res.json();
};

export const fetchLeadsByDealStatus = async (key, page = 1, limit = 5, agentId = null) => {
  const params = new URLSearchParams({ page, limit });
  if (key === "pending") params.set("status", "pending");
  else if (key) params.set("deal_status", key);
  if (agentId) params.set("agent_id", agentId); 

  const res = await fetch(
    `${domain}/api/v1/lead/get-leads-by-status?${params.toString()}`,
    { method: "GET", headers: getAuthHeaders() }
  );
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to fetch leads");
  return data;
};

// Now accepts agentId, and uses dealStatusStats from the stats endpoint properly
export async function fetchLeadStats(agentId = null) {
  const params = new URLSearchParams({ includeStats: "true", statsOnly: "true" });
  if (agentId) params.set("agent_id", agentId);

  const res = await fetch(`${domain}/api/v1/lead/get?${params.toString()}`, {
    headers: getAuthHeaders(),
  });

  const data = await res.json();

  return {
    success: data.success,
    total: data.total,
    dealStatusStats: data.dealStatusStats || {},
    statusStats: data.statusStats || {},
  };
}